import {Component, OnInit, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {FormService} from '../../services/form.service'
import {Attachment} from '../../types/attachment'
import {AttachmentType} from '../../types/attachment-type';
import {Document} from '../../types/document';
import {Proxy} from '../../types/proxy';
import {Shareholder} from '../../types/shareholder';
import {identityTypes} from '../../types/identity-type';
import {EntityComponent} from '../entity/entity.component';
import {AssigneeComponent} from "../assignee/assignee.component";
import {AttachmentsComponent} from "../attachments/attachments.component";
import {ContactComponent} from "../contact/contact.component";
import {ConfirmationService, MessageService} from 'primeng/api';
import {CompanyType} from "../../types/company-type";
import {ShareholdersComponent} from "../shareholders/shareholders.component";
import {SummaryComponent} from "../summary/summary.component";
import {PartnersComponent} from "../partners/partners.component";
import {Constants} from "../../types/constants";


enum Tab {
  ENTITY,
  SHAREHOLDERS,
  PARTNERS,
  ASSIGNEE,
  ATTACHMENTS,
  CONTACT,
  SUMMARY
}

@Component({
  selector: 'rpk-component',
  templateUrl: './rpk.component.html',
  styleUrls: ['./rpk.component.css'],
  providers: [ConfirmationService]
})
export class RpkComponent implements OnInit {
  AttachmentType = AttachmentType;
  CompanyType = CompanyType;
  Tab = Tab;
  identityTypes = identityTypes;
  
  tabsMap : Map<CompanyType, Tab[]> = new Map([
    [ CompanyType.PHYSICAL, [Tab.ENTITY, Tab.ASSIGNEE, Tab.ATTACHMENTS, Tab.CONTACT, Tab.SUMMARY]],
    [ CompanyType.LEGAL, [Tab.ENTITY, Tab.SHAREHOLDERS, Tab.ASSIGNEE, Tab.ATTACHMENTS, Tab.CONTACT, Tab.SUMMARY]],
    [ CompanyType.PARTNERSHIP, [Tab.ENTITY, Tab.PARTNERS, Tab.ASSIGNEE, Tab.ATTACHMENTS, Tab.CONTACT, Tab.SUMMARY]]
  ])

  titlesMap : Map<Tab, string> = new Map([
    [Tab.ENTITY, "Wpisz PESEL, pozostałe dane są automatycznie pobierane z CEIDG. Zweryfikuj je i ewentualnie popraw informacje w CEIDG."],
    [Tab.ASSIGNEE, "Zaznacz kwadrat jeśli masz pełnomocnictwo wnioskodawcy"],
    [Tab.SHAREHOLDERS, "W miejscu gwiazdek uzupełnij odpowiednie dane zgodne z KRS"],
    [Tab.PARTNERS, "W miejscu gwiazdek uzupełnij odpowiednie dane zgodne z KRS"],
    [Tab.ATTACHMENTS, "Dołącz przygotowane załączniki w odpowiednich formatach"],
    [Tab.CONTACT, "Uzupełnij dane kontaktowe"],
    [Tab.SUMMARY, "Zweryfikuj wprowadzone dane do formularza wniosku"]
  ])
  NON_PHYSICAL_ENTITY_TITLE : string = "Dane są automatycznie pobierane z Krajowego Rejestru Sądowego. Zweryfikuj je i ewentualnie popraw w KRS.";

  companyType : CompanyType = CompanyType.UNKNOWN;
  attachmentByTypeArray = new Array<Attachment>(Object.keys(AttachmentType).length);

  document !: Document;
  activeTab : Tab = Tab.ENTITY;
  activeIndex: number = 0;
  isAssignee : boolean = false;
  title : string = "";
  proxy : Proxy = {};

  shareholders : Shareholder[] = [];
  dataAgree : boolean = false;
  conditionsAgree : boolean = false;
  rodoAgree : boolean = false;
  noPesel : boolean = false;
  registryDataInvalid : boolean = false;
  onlyPesels : boolean = false;
  sendingProposal : boolean = false;

  @ViewChild(EntityComponent) entityComponent !: EntityComponent;
  @ViewChild(AssigneeComponent) assigneeComponent !: AssigneeComponent;
  @ViewChild(AttachmentsComponent) attachmentsComponent !: AttachmentsComponent;
  @ViewChild(ContactComponent) contactComponent !: ContactComponent;
  @ViewChild(ShareholdersComponent) shareholdersComponent !: ShareholdersComponent;
  @ViewChild(PartnersComponent) partnersComponent !: PartnersComponent;
  @ViewChild(SummaryComponent) summaryComponent !: SummaryComponent;

  constructor(
    private router: Router,
    private formService: FormService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService) {
  }

  ngOnInit(): void {
    this.companyType = CompanyType.UNKNOWN;
    this.document = this.formService.document;
    if (this.document.id == 0)
      this.router.navigate(['/choose-form']);
    this.activeTab = Tab.ENTITY;
    this.activeIndex = 0;
    this.proxy = {};
    this.shareholders = [];
    this.dataAgree = false;
    this.conditionsAgree = false;
    this.rodoAgree = false;
    this.sendingProposal = false;
    if (this.document.company.type.startsWith("OF_P_"))
      this.companyType = CompanyType.PHYSICAL;
    if (this.document.company.type.startsWith("SP_"))
      this.companyType = CompanyType.PARTNERSHIP;
    if (this.document.company.type.startsWith("O_PR_"))
      this.companyType = CompanyType.LEGAL;
    if (this.companyType == CompanyType.UNKNOWN) {
      this.messageService.add({
        severity:'error',
        detail: "Błędny typ podmiotu" }
      );
      this.router.navigate(['/choose-form']);
      return;
    }
    this.title = this.companyType==CompanyType.PHYSICAL ? this.titlesMap.get(Tab.ENTITY)! : this.NON_PHYSICAL_ENTITY_TITLE;
  }
  
  formInvalid() : boolean {
    if (this.summaryComponent.formInvalid()
      || this.entityComponent.formInvalid()
      || this.assigneeComponent.formInvalid()
      || this.attachmentsComponent.formInvalid()
      || this.contactComponent.formInvalid()
      || (this.companyType == CompanyType.LEGAL && this.shareholdersComponent.formInvalid())
      || (this.companyType == CompanyType.PARTNERSHIP && this.partnersComponent.formInvalid())
    )
      return true;
    return false;
  }
  
  tabChanged(event: any) {
    switch (this.activeTab) {
      case Tab.ENTITY:
        this.entityComponent.markFormAsTouched();
        break;
      case Tab.SHAREHOLDERS:
        this.shareholdersComponent.setFormDirty(true);
        break;
      case Tab.PARTNERS:
        this.partnersComponent.setFormDirty(true);
        break;
      case Tab.ASSIGNEE:
        if (this.isAssignee)
          this.assigneeComponent.markFormAsTouched();
        this.assigneeComponent.setFormDirty(true);
        break;
      case Tab.ATTACHMENTS:
        this.attachmentsComponent.setFormDirty(true);
        break;
      case Tab.CONTACT:
        this.contactComponent.markFormAsTouched();
        this.contactComponent.setFormDirty(true);
        break;
      case Tab.SUMMARY:
        this.summaryComponent.setFormDirty(true);
        break;
    }
    this.activeTab = this.tabsMap.get(this.companyType)![event.index];
    if (this.activeTab == Tab.ENTITY && this.companyType != CompanyType.PHYSICAL)
      this.title = this.NON_PHYSICAL_ENTITY_TITLE;
    else
      this.title = this.titlesMap.get(this.activeTab)!;
    if (this.companyType == CompanyType.PARTNERSHIP && this.activeTab == Tab.SUMMARY)
      this.summaryComponent.refreshPartners();
  }

  chooseForm(): void {
    this.confirmationService.confirm({
      message: "Czy na pewno chcesz anulować wniosek?\nWprowadzone dane nie zostaną zapisane?",
      accept: () => this.router.navigate(['/choose-form'])
    })
  }

  goBack(): void {
    this.activeIndex = this.activeIndex - 1;
    this.tabChanged({index: this.activeIndex});
  }

  goForward(): void {
    this.activeIndex = this.activeIndex + 1;
    this.tabChanged({index: this.activeIndex});
  }

  sendProposal(): void {
    if (this.formInvalid()) {
      if (this.registryDataInvalid
        || (this.companyType != CompanyType.PHYSICAL && this.document.company.shareholders?.length == 0))
        this.messageService.add({
          severity:'warn',
          detail: "Nie można złożyć wniosku. Skontaktuj się z " + (this.companyType == CompanyType.PHYSICAL ? 'CEIDG' : 'KRS') + " aby poprawić dane" }
        );
      else
        this.messageService.add({
          severity:'warn',
          detail: "Popraw błędy" }
        );
      this.entityComponent.markFormAsTouched();
      if (this.companyType == CompanyType.LEGAL)
        this.shareholdersComponent.setFormDirty(true);
      if (this.companyType == CompanyType.PARTNERSHIP)
        this.partnersComponent.setFormDirty(true);
      if (this.isAssignee)
        this.assigneeComponent.markFormAsTouched();
      this.assigneeComponent.setFormDirty(true);
      this.attachmentsComponent.setFormDirty(true);
      this.contactComponent.markFormAsTouched();
      this.contactComponent.setFormDirty(true);
      this.summaryComponent.setFormDirty(true);
      return;
    }
    // copy file ids
    
    this.document.company.registerId = this.attachmentByTypeArray[AttachmentType.REGISTER]!.id;
    this.document.company.paymentId = this.attachmentByTypeArray[AttachmentType.PAYMENT]!.id;
    if (this.companyType == CompanyType.PHYSICAL)
      this.document.company.personalData.krkId = this.attachmentByTypeArray[AttachmentType.KRK]!.id;
    
    if (this.isAssignee) {
      this.document.company.proxy = this.proxy;
      this.document.company.proxy.authId = this.attachmentByTypeArray[AttachmentType.AUTHORIZATION].id;
      this.document.company.proxy.authPaymentId = this.attachmentByTypeArray[AttachmentType.AUTH_PAYMENT].id;
    } else
      this.document.company.proxy = undefined;

    this.sendingProposal = true;
    this.formService.sendProposal(this.document)
    .subscribe(
      response => {
       this.formService.proposalSignature = response.body.message;
       this.formService.proposalId = this.document.id;
       this.formService.companyTypeId = this.document.company.type;
       this.router.navigate(["/sign"]);
      },
      (error: HttpErrorResponse) => {
        this.messageService.add({
          severity: (error.status == 400 && error.error.message != Constants.GenericError) ? 'warn' : 'error',
          detail: error.error.message ?? Constants.GenericError }
        );
        this.sendingProposal = false;
      }
    );
  }
}
